import { Base } from "../../framework/base";
import { stateGreen, stateOrange, stateRed, stateYellow } from "../consts";
import { ApprovalType, RiskTreatmentState, RiskType } from "../enums";
import { Translations } from "../translations";
import { IModifyUser, ModifyUser } from "../user/modifyUser";

export interface IRiskListItem {
    id: string;
    riskType: RiskType;
    unitCode: string;
    code: string;
    owner: string;
    title: string;
    description: string;
    threat: string;
    controls: string;
    riskCategoryCode: string;
    hasAssesment: boolean;
    riskAssessmentId: string;
    assessor: string;
    nextAssesmentDate?: number;
    riskLevel: number;
    residualRisk: number;
    treatmentTargetDate?: number;
    riskTreatmentState: RiskTreatmentState;
    treatments: string;
    approval?: ApprovalType;
    opportunity: boolean;
    opportunityBenefit: string;
    opportunityDescription: string;
    isRead: boolean;
    locked: boolean;
    canEdit: boolean;
    createUser: IModifyUser;
    modifyUser: IModifyUser;
    publishUser: IModifyUser;
    archiveUser: IModifyUser;
}

export class RiskListItem implements IRiskListItem {
    id: string;
    riskType: RiskType;
    unitCode: string;
    code: string;
    owner: string;
    title: string;
    description: string;
    threat: string;
    controls: string;
    riskCategoryCode: string;
    hasAssesment: boolean;
    riskAssessmentId: string;
    assessor: string;
    nextAssesmentDate?: number;
    riskLevel: number;
    residualRisk: number;
    treatmentTargetDate?: number;
    riskTreatmentState: RiskTreatmentState;
    treatments: string;
    approval?: ApprovalType;
    opportunity: boolean;
    opportunityBenefit: string;
    opportunityDescription: string;
    isRead: boolean;
    locked: boolean;
    canEdit: boolean;
    createUser: ModifyUser;
    modifyUser: ModifyUser;
    publishUser: ModifyUser;
    archiveUser: ModifyUser;

    constructor();
    constructor(obj: IRiskListItem);
    constructor(obj?: any) {
        this.id = obj && obj.id || "";
        this.riskType = obj && obj.riskType || RiskType.Risk;
        this.unitCode = obj && obj.unitCode || "";
        this.code = obj && obj.code || "";
        this.owner = obj && obj.owner || "";
        this.title = obj && obj.title || "";
        this.description = obj && obj.description || "";
        this.threat = obj && obj.threat || "";
        this.controls = obj && obj.controls || "";
        this.riskCategoryCode = obj && obj.riskCategoryCode || "";
        this.hasAssesment = obj && obj.hasAssesment || false;
        this.riskAssessmentId = obj && obj.riskAssessmentId || "";
        this.assessor = obj && obj.assessor || "";
        this.nextAssesmentDate = obj && obj.nextAssesmentDate || null;
        this.riskLevel = obj && obj.riskLevel || 0;
        this.residualRisk = obj && obj.residualRisk || 0;
        this.treatmentTargetDate = obj && obj.treatmentTargetDate || null;
        this.riskTreatmentState = obj && obj.riskTreatmentState || RiskTreatmentState.NotNeeded;
        this.treatments = obj && obj.treatments || "";
        this.approval = obj && obj.approval || null;
        this.opportunity = obj && obj.opportunity || false;
        this.opportunityBenefit = obj && obj.opportunityBenefit || "";
        this.opportunityDescription = obj && obj.opportunityDescription || "";
        this.isRead = obj && obj.isRead || false;
        this.locked = obj && obj.locked || false;
        this.canEdit = obj && obj.canEdit || false;
        this.createUser = new ModifyUser(obj ? obj.createUser : null);
        this.modifyUser = new ModifyUser(obj ? obj.modifyUser : null);
        this.publishUser = obj?.publishUser ? new ModifyUser(obj.publishUser) : null;
        this.archiveUser = obj?.archiveUser ? new ModifyUser(obj.archiveUser) : null;
    }

    static getRiskTypeText(riskType: RiskType, opportunity: boolean): string {
        return riskType === RiskType.Risk
            ? (Translations.Risk + (opportunity ? ` + ${Translations.Opportunity}` : ""))
            : Translations.Opportunity;
    }

    static getRiskLikelihoodText(likelihood: number): string {
        switch (likelihood) {
        case 2: return `${likelihood} - ${Translations.RiskLikelihood2}`;
        case 3: return `${likelihood} - ${Translations.RiskLikelihood3}`;
        case 4: return `${likelihood} - ${Translations.RiskLikelihood4}`;
        case 5: return `${likelihood} - ${Translations.RiskLikelihood5}`;
        default: return `${likelihood} - ${Translations.RiskLikelihood1}`;
        }
    }

    static getRiskImpactText(impact: number): string {
        switch (impact) {
        case 2: return `${impact} - ${Translations.RiskImpact2}`;
        case 3: return `${impact} - ${Translations.RiskImpact3}`;
        case 4: return `${impact} - ${Translations.RiskImpact4}`;
        case 5: return `${impact} - ${Translations.RiskImpact5}`;
        default: return `${impact} - ${Translations.RiskImpact1}`;
        }
    }

    static getRiskControlEfficiencyText(controlEffiency: number): string {
        switch (controlEffiency) {
        case 1: return Translations.DoesNotWork;
        case 2: return Translations.PartiallyWorks;
        case 3: return Translations.WorksWell;
        default: return Translations.NotEvaluated;
        }
    }

    static getRiskTreatmentStateText(state: RiskTreatmentState): string {
        switch (state) {
        case RiskTreatmentState.NotNeeded: return Translations.RiskTreatmentStateNotNeeded;
        case RiskTreatmentState.TreatmentsSpecified: return Translations.RiskTreatmentStateTreatmentsSpecified;
        case RiskTreatmentState.InProgress: return Translations.RiskTreatmentStateInProgress;
        case RiskTreatmentState.Completed: return Translations.RiskTreatmentStateCompleted;
        default: return Translations.RiskTreatmentStateLate;
        }
    }

    static getRiskLevelTextBasic(riskLevel: number): string {
        if (riskLevel <= 3) {
            return Translations.RiskLevelNegligible;
        }
        if (riskLevel <= 6) {
            return Translations.RiskLevelLow;
        }
        if (riskLevel <= 9) {
            return Translations.RiskLevelMedium;
        }
        if (riskLevel <= 14) {
            return Translations.RiskLevelHigh;
        }
        return Translations.RiskLevelCritical;
    }

    static getRiskLevelText(riskLevel: number): string {
        return `${riskLevel} - ${RiskListItem.getRiskLevelTextBasic(riskLevel)}`;
    }

    static getRiskLevelCategory(riskLevel: number): number {
        if (riskLevel <= 3) {
            return 0;
        }
        if (riskLevel <= 6) {
            return 1;
        }
        if (riskLevel <= 9) {
            return 2;
        }
        if (riskLevel <= 14) {
            return 3;
        }
        return 4;
    }

    static getRiskLevelColor(riskLevel: number): string {
        if (riskLevel <= 3) {
            return "#b8ddb8";
        }
        if (riskLevel <= 6) {
            return "#f0dd9f";
        }
        if (riskLevel <= 9) {
            return "#f0dd9f";
        }
        if (riskLevel <= 14) {
            return "#f0c28a";
        }
        return "#efb4b8";
    }

    static getRiskLevelColorMobile(riskLevel: number): string {
        if (riskLevel <= 3) {
            return stateGreen;
        }
        if (riskLevel <= 6) {
            return stateYellow;
        }
        if (riskLevel <= 9) {
            return stateYellow;
        }
        if (riskLevel <= 14) {
            return stateOrange;
        }
        return stateRed;
    }

    static getRiskTraceabilityText(riskLevel: number): string {
        if (riskLevel <= 3) {
            return Translations.RiskTraceabilityLow;
        }
        if (riskLevel <= 9) {
            return Translations.RiskTraceabilityMedium;
        }
        return Translations.RiskTraceabilityHigh;
    }

    static getRiskTraceabilityColor(riskLevel: number): string {
        return RiskListItem.getRiskLevelColor(riskLevel);
    }

    static sortRiskListItemsByColumn(items: IRiskListItem[], column: string, asc: boolean): IRiskListItem[] {
        if (!items || items.length < 2) return items;
        if (column === "code") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * Base.strCompare(a.code, b.code)); });
        } else if (column === "title") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.strCompare(a.title, b.title) + Base.strCompare(a.code, b.code))); });
        } else if (column === "riskCategoryCode") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.strCompare(a.riskCategoryCode, b.riskCategoryCode) + Base.strCompare(a.code, b.code))); });
        } else if (column === "owner") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.strCompare(a.owner, b.owner) + Base.strCompare(a.code, b.code))); });
        } else if (column === "description") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.strCompare(a.description, b.description) + Base.strCompare(a.code, b.code))); });
        } else if (column === "controls") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.strCompare(a.controls, b.controls) + Base.strCompare(a.code, b.code))); });
        } else if (column === "riskLevel") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.numCompare(a.riskLevel, b.riskLevel) + Base.strCompare(a.code, b.code))); });
        } else if (column === "residualRisk") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.numCompare(a.residualRisk, b.residualRisk) + Base.strCompare(a.code, b.code))); });
        } else if (column === "riskTreatmentState") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.numCompare(a.riskTreatmentState, b.riskTreatmentState) + Base.strCompare(a.code, b.code))); });
        } else if (column === "treatmentTargetDate") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.numCompare(a.treatmentTargetDate ?? 0, b.treatmentTargetDate ?? 0) + Base.strCompare(a.code, b.code))); });
        } else if (column === "treatments") {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.strCompare(a.treatments, b.treatments) + Base.strCompare(a.code, b.code))); });
        } else {
            return items.sort((a: IRiskListItem, b: IRiskListItem) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.numCompare(a.createUser.date, b.createUser.date) + Base.strCompare(a.code, b.code))); });
        }
    }
}